import { supabase } from "./supabaseClient";

export const AUTH_STORAGE_KEYS = {
  ACCESS_TOKEN: "token",
  REFRESH_TOKEN: "supabase_refresh_token",
  AUTH_USER: "supabase_auth_user",
};

/* ── session helpers ─────────────────────────────────────── */

function persistSession(session, user) {
  if (session?.access_token) {
    localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, session.access_token);
  }
  if (session?.refresh_token) {
    localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, session.refresh_token);
  }
  if (user) {
    localStorage.setItem(AUTH_STORAGE_KEYS.AUTH_USER, JSON.stringify(user));
  }
}

function clearSession() {
  localStorage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(AUTH_STORAGE_KEYS.AUTH_USER);
}

function readStoredAuthUser() {
  const raw = localStorage.getItem(AUTH_STORAGE_KEYS.AUTH_USER);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/* ── user normalisation ──────────────────────────────────── */

function normalizeUser(authUser, profile) {
  // Default to "patient" (not "user") to match the DB user_role enum
  const roleFromData =
    profile?.role ||
    authUser?.user_metadata?.role ||
    authUser?.app_metadata?.role ||
    "patient";

  const adminEmails = String(process.env.REACT_APP_ADMIN_EMAILS || "")
    .split(",")
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);

  const email = authUser?.email || profile?.email || "";
  const isAdminEmail = !!email && adminEmails.includes(String(email).trim().toLowerCase());

  const role = isAdminEmail ? "admin" : roleFromData;

  return {
    id: profile?.id ?? authUser?.id,
    auth_id: authUser?.id || profile?.auth_user_id || null,
    email,
    name: profile?.name || authUser?.user_metadata?.name || authUser?.user_metadata?.full_name || "",
    phone: profile?.phone || authUser?.user_metadata?.phone || "",
    avatar_url:
      profile?.avatar_url ||
      authUser?.user_metadata?.avatar_url ||
      authUser?.user_metadata?.picture ||
      "",
    role,
    created_at: profile?.created_at || authUser?.created_at,
  };
}

async function getProfileById(id) {
  if (!id) return null;
  try {
    const { data, error } = await supabase
      .from("users")
      .select("*")
      .eq("id", id)
      .maybeSingle();
    if (error) return null;
    return data;
  } catch {
    return null;
  }
}

async function getProfileByEmail(email) {
  if (!email) return null;
  const normalizedEmail = email.trim().toLowerCase();
  try {
    const { data, error } = await supabase
      .from("users")
      .select("*")
      .ilike("email", normalizedEmail)
      .maybeSingle();
    if (error) return null;
    return data;
  } catch {
    return null;
  }
}

async function getProfileByAuthUserId(authUserId) {
  if (!authUserId) return null;
  try {
    const { data, error } = await supabase
      .from("users")
      .select("*")
      .eq("auth_user_id", authUserId)
      .maybeSingle();
    if (error) return null;
    return data;
  } catch {
    return null;
  }
}

async function getProfileByAuthUser(authUser) {
  if (!authUser) return null;

  const byDirectId = await getProfileById(authUser.id);
  if (byDirectId) return byDirectId;

  const byAuthUserId = await getProfileByAuthUserId(authUser.id);
  if (byAuthUserId) return byAuthUserId;

  const byEmail = await getProfileByEmail(authUser.email);
  if (byEmail) return byEmail;

  return null;
}

async function upsertProfile(profile) {
  if (!profile) return;
  try {
    if (profile.id !== undefined && profile.id !== null && profile.id !== "") {
      await supabase.from("users").upsert(profile, { onConflict: "id" });
      return;
    }

    const profileWithoutId = { ...profile };
    delete profileWithoutId.id;
    if (!profileWithoutId.email) return;

    await supabase.from("users").upsert(profileWithoutId, { onConflict: "email" });
  } catch {
    // Profile table may be protected by RLS; auth can still work without it.
  }
}

/* ── OTP helpers (demo – stored client-side) ─────────────── */

/* ── misc helpers ────────────────────────────────────────── */

function ensureIsoDate(date) {
  return new Date(date).toISOString().split("T")[0];
}

function toDisplayAppointment(row) {
  return {
    ...row,
    service: row.service_name || row.service || row.service_id || "General Consultation",
    doctor: row.doctor_name || row.doctor || row.doctor_id || "",
  };
}

async function resolveAppointmentUserId(
  user,
  { fallbackEmail, fallbackName, fallbackPhone, createIfMissing = false } = {}
) {
  if (!user && !fallbackEmail) return null;

  if (user?.id !== undefined && user?.id !== null && user?.id !== "") {
    const byId = await getProfileById(user.id);
    if (byId?.id !== undefined && byId?.id !== null) return byId.id;
  }

  const authUserId = user?.auth_id || user?.id;
  if (authUserId) {
    const byAuthUserId = await getProfileByAuthUserId(authUserId);
    if (byAuthUserId?.id !== undefined && byAuthUserId?.id !== null) return byAuthUserId.id;
  }

  const normalizedEmail = (user?.email || fallbackEmail || "").trim().toLowerCase();
  if (normalizedEmail) {
    const byEmail = await getProfileByEmail(normalizedEmail);
    if (byEmail?.id !== undefined && byEmail?.id !== null) return byEmail.id;
  }

  if (!createIfMissing || !normalizedEmail) return null;

  try {
    const insertRow = {
      email: normalizedEmail,
      name: user?.name || fallbackName || "",
      phone: user?.phone || fallbackPhone || "",
      role: user?.role || "user",
      avatar_url: user?.avatar_url || "",
    };

    const { data, error } = await supabase
      .from("users")
      .insert(insertRow)
      .select("id")
      .single();

    if (!error && data?.id !== undefined && data?.id !== null) return data.id;
  } catch {
    // RLS or schema restrictions can block insert; fallback lookup is below.
  }

  const byEmailAfterInsert = await getProfileByEmail(normalizedEmail);
  return byEmailAfterInsert?.id ?? null;
}

export function getStoredAuthUserRole() {
  const stored = readStoredAuthUser();
  if (!stored) return null;
  return stored?.role || stored?.user_metadata?.role || stored?.app_metadata?.role || null;
}

/* ── main API object ─────────────────────────────────────── */

export const supabaseApi = {
  /* ---------- auth ---------- */

  async getCurrentUser() {
    const { data: { user: authUser }, error } = await supabase.auth.getUser();
    if (error || !authUser) throw new Error(error?.message || "Not authenticated");
    const profile = await getProfileByAuthUser(authUser);
    const user = normalizeUser(authUser, profile);
    localStorage.setItem(AUTH_STORAGE_KEYS.AUTH_USER, JSON.stringify(user));
    return user;
  },

  async login(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    if (!data?.session?.access_token) {
      throw new Error("Sign-in succeeded but no session was returned.");
    }
    let profile = null;
    try {
      profile = await getProfileByAuthUser(data.user);
    } catch (e) {
      console.warn("Profile lookup failed (continuing with auth user only):", e);
    }
    const user = normalizeUser(data.user, profile);
    persistSession(data.session, user);
    return { token: data.session.access_token, user };
  },

  async register(name, email, phone, password) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { name, phone, role: "user" } },
    });
    if (error) throw error;

    if (!data?.session?.access_token) {
      throw new Error(
        "Registration succeeded but no session was returned. Disable email confirmation or verify the account first."
      );
    }

    const profile = await getProfileByAuthUser(data.user);
    const user = normalizeUser(data.user, profile);
    persistSession(data.session, user);

    await upsertProfile({
      id: profile?.id,
      auth_user_id: data.user?.id,
      email: user.email,
      name: user.name || name,
      phone: user.phone || phone || "",
      avatar_url: user.avatar_url || "",
      role: user.role || "user",
    });

    return { token: data.session.access_token, user };
  },

  async signInWithGoogle() {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: window.location.origin,
      },
    });
    if (error) throw error;
    return data;
  },

  async handleOAuthUser() {
    const { data: { user: authUser }, error } = await supabase.auth.getUser();
    if (error || !authUser) throw new Error(error?.message || "Not authenticated");
    const profile = await getProfileByAuthUser(authUser);
    const user = normalizeUser(authUser, profile);

    // Upsert profile for OAuth users who may not have one yet
    if (!profile) {
      await upsertProfile({
        id: profile?.id,
        auth_user_id: authUser.id,
        email: user.email,
        name: user.name || authUser.user_metadata?.full_name || "",
        phone: user.phone || "",
        avatar_url: user.avatar_url || authUser.user_metadata?.avatar_url || authUser.user_metadata?.picture || "",
        role: user.role || "user",
      });
    }

    localStorage.setItem(AUTH_STORAGE_KEYS.AUTH_USER, JSON.stringify(user));
    return user;
  },

  async logout() {
    await supabase.auth.signOut();
    clearSession();
  },

  /* ---------- services ---------- */

  async getServices() {
    const { data, error } = await supabase
      .from("services")
      .select("*")
      .order("id", { ascending: true });
    if (!error && data?.length) return data;
    return [
      { id: "General Checkup", name: "General Checkup", price: 49 },
      { id: "Teeth Cleaning", name: "Teeth Cleaning", price: 79 },
      { id: "Root Canal", name: "Root Canal", price: 249 },
      { id: "Dental Implants", name: "Dental Implants", price: 999 },
    ];
  },

  /* ---------- doctors ---------- */

  async getDoctors() {
    const { data, error } = await supabase
      .from("doctors")
      .select("*")
      .order("id", { ascending: true });
    if (!error && data?.length) return data;
    return [
      { id: "Dr. Sarah Johnson", name: "Dr. Sarah Johnson", specialty: "General Dentistry" },
      { id: "Dr. Michael Chen", name: "Dr. Michael Chen", specialty: "Orthodontics" },
      { id: "Dr. Emily Davis", name: "Dr. Emily Davis", specialty: "Endodontics" },
      { id: "Dr. James Wilson", name: "Dr. James Wilson", specialty: "Oral Surgery" },
    ];
  },

  /* ---------- appointments ---------- */

  async getAvailableSlots(dateStr) {
    const defaultSlots = [
      "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
      "12:00", "12:30", "14:00", "14:30", "15:00", "15:30",
      "16:00", "16:30", "17:00", "17:30",
    ];

    try {
      const { data } = await supabase
        .from("appointments")
        .select("time, status")
        .eq("date", dateStr);
      const blocked = new Set(
        (data || [])
          .filter((row) => row?.status !== "cancelled")
          .map((row) => row?.time)
          .filter(Boolean)
      );
      return defaultSlots.filter((slot) => !blocked.has(slot));
    } catch {
      return defaultSlots;
    }
  },

  async createAppointment(payload, _token, user) {
    const resolvedUserId = await resolveAppointmentUserId(user, {
      fallbackEmail: payload.email,
      fallbackName: payload.patient_name,
      fallbackPhone: payload.phone,
      createIfMissing: true,
    });

    const row = {
      user_id: resolvedUserId,
      patient_name: payload.patient_name,
      email: payload.email,
      phone: payload.phone,
      service: payload.service,
      doctor: payload.doctor || null,
      notes: payload.notes || null,
      date: payload.date,
      time: payload.time,
      status: "pending",
    };
    const { error } = await supabase.from("appointments").insert(row);
    if (error) throw error;

    // Send booking confirmation email (fire-and-forget, won't break booking if it fails)
    if (payload.email) {
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      fetch(`${supabaseUrl}/functions/v1/send-booking-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          patient_name: payload.patient_name,
          email: payload.email,
          service: payload.service,
          doctor: payload.doctor || null,
          date: payload.date,
          time: payload.time,
          phone: payload.phone || null,
          notes: payload.notes || null,
        }),
      }).catch((e) => console.warn("Email notification failed (non-critical):", e));
    }
  },

  async getMyAppointments(_token, user) {
    if (!user?.id && !user?.email) return [];

    const resolvedUserId = await resolveAppointmentUserId(user, {
      fallbackEmail: user?.email,
      createIfMissing: false,
    });

    let query = supabase.from("appointments").select("*");
    if (resolvedUserId !== undefined && resolvedUserId !== null) {
      query = query.eq("user_id", resolvedUserId);
    } else {
      query = query.ilike("email", user.email);
    }
    const { data, error } = await query.order("date", { ascending: false }).order("time", { ascending: false });
    if (error) throw error;
    return (data || []).map(toDisplayAppointment);
  },

  async cancelAppointment(id) {
    const { error } = await supabase
      .from("appointments")
      .update({ status: "cancelled" })
      .eq("id", id);
    if (error) throw error;
  },

  /* ---------- clinics ---------- */

  async getClinics() {
    const { data, error } = await supabase
      .from("clinics")
      .select("*")
      .order("id", { ascending: false });
    if (error) throw error;
    return data || [];
  },

  /* ---------- admin ---------- */

  async getAdminStats() {
    const today = ensureIsoDate(new Date());
    const safeCount = (p) =>
      p.then((r) => ({ count: r.error ? 0 : r.count ?? 0 })).catch(() => ({ count: 0 }));

    try {
      const [
        { count: totalAppointments },
        { count: todayAppointments },
        { count: confirmedAppointments },
        { count: completedAppointments },
        { count: totalUsers },
        { count: totalClinics },
      ] = await Promise.all([
        safeCount(supabase.from("appointments").select("id", { count: "exact", head: true })),
        safeCount(supabase.from("appointments").select("id", { count: "exact", head: true }).eq("date", today)),
        safeCount(supabase.from("appointments").select("id", { count: "exact", head: true }).eq("status", "confirmed")),
        safeCount(supabase.from("appointments").select("id", { count: "exact", head: true }).eq("status", "completed")),
        safeCount(supabase.from("users").select("id", { count: "exact", head: true })),
        safeCount(supabase.from("clinics").select("id", { count: "exact", head: true })),
      ]);

      return {
        total_appointments: totalAppointments,
        today_appointments: todayAppointments,
        confirmed_appointments: confirmedAppointments,
        completed_appointments: completedAppointments,
        total_users: totalUsers,
        total_clinics: totalClinics,
      };
    } catch {
      return {
        total_appointments: 0,
        today_appointments: 0,
        confirmed_appointments: 0,
        completed_appointments: 0,
        total_users: 0,
        total_clinics: 0,
      };
    }
  },

  async getAdminAppointments() {
    const { data, error } = await supabase
      .from("appointments")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw error;
    return (data || []).map(toDisplayAppointment);
  },

  async getAdminUsers() {
    try {
      const { data, error } = await supabase
        .from("users")
        .select("id, name, email, phone, role, created_at")
        .order("created_at", { ascending: false });
      if (error) return [];
      return data || [];
    } catch {
      return [];
    }
  },

  async getAdminClinics() {
    return this.getClinics();
  },

  async updateAppointmentStatus(id, status) {
    const { error } = await supabase
      .from("appointments")
      .update({ status })
      .eq("id", id);
    if (error) throw error;
  },

  async deleteAppointment(id) {
    const { error } = await supabase
      .from("appointments")
      .delete()
      .eq("id", id);
    if (error) throw error;
  },

  async addClinic(clinic) {
    // Fetch a fresh session to ensure auth token is attached
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error("You must be logged in as an admin to add clinics.");
    const { error } = await supabase.from("clinics").insert(clinic);
    if (error) {
      console.error("addClinic Supabase error:", error);
      throw new Error(error.message || "Failed to add clinic. Make sure you are logged in as admin.");
    }
  },

  async deleteClinic(id) {
    const { error } = await supabase
      .from("clinics")
      .delete()
      .eq("id", id);
    if (error) throw error;
  },

  async changePassword(_token, email, currentPassword, newPassword) {
    // Verify current password
    const { error: loginError } = await supabase.auth.signInWithPassword({
      email,
      password: currentPassword,
    });
    if (loginError) throw new Error("Current password is incorrect.");

    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
    return { message: "Password updated successfully." };
  },

  /* ---------- site settings ---------- */

  async getSiteSettings() {
    try {
      const { data, error } = await supabase
        .from("site_settings")
        .select("*")
        .limit(1)
        .single();
      if (error || !data) return null;
      return data;
    } catch {
      return null;
    }
  },

  /* ---------- dental problems / treatments ---------- */

  async getDentalProblems() {
    try {
      const { data, error } = await supabase
        .from("dental_problems")
        .select("*")
        .order("id", { ascending: true });
      if (!error && data?.length) return data;
    } catch {
      // fall through
    }
    return null;
  },

  /* ---------- hero stats ---------- */

  async getHeroStats() {
    try {
      const { data, error } = await supabase
        .from("hero_stats")
        .select("*")
        .order("id", { ascending: true });
      if (!error && data?.length) return data;
    } catch {
      // fall through
    }
    return null;
  },
};
